import { Lock } from 'lucide-react'
import { cn } from '@/lib/cn'
import { teamColor, teamLabel } from '@/lib/teamColors'
import { kickoffLabel } from '@/lib/time'
import {
  gameStatusLabel,
  LOCK_TONE_CLASS,
  lockTone,
  OUTCOME_CLASS,
  OUTCOME_LABEL,
  pickOutcome,
  type BoardGame,
  type BoardPick,
} from './pickState'
import type { BoardMember } from './PickGrid'

/* One card per game, members filed under the team they took.

   The outcome is a property of (game, team), not of the member, so a whole
   column shares it and only the Lock icons differ per person. That is what makes
   this readable on a phone: two lists, one right and one wrong, rather than ten
   individually-shaded cells. */
export default function PickCards({
  games,
  picks,
  members,
  tiePolicy,
}: {
  games: BoardGame[]
  picks: BoardPick[]
  members: BoardMember[]
  tiePolicy: string
}) {
  const nameByUser = new Map(members.map((member) => [member.userId, member.name]))

  const byGame = new Map<string, BoardPick[]>()
  for (const pick of picks) {
    const list = byGame.get(pick.game_id)
    if (list) list.push(pick)
    else byGame.set(pick.game_id, [pick])
  }

  const side = (game: BoardGame, team: string, forGame: BoardPick[]) => {
    const outcome = pickOutcome(game, team, tiePolicy)
    const takers = forGame.filter((pick) => pick.picked_team === team)
    const { nickname } = teamLabel(team)

    return (
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="h-3 w-1 shrink-0"
            style={{ backgroundColor: teamColor(team) }}
          />
          <span
            className={cn('text-body inline-block px-1.5 py-0.5 font-mono', OUTCOME_CLASS[outcome])}
          >
            {team}
          </span>
          <span className="sr-only">{OUTCOME_LABEL[outcome]}</span>
        </div>

        <p className="text-ink-dim mt-1 truncate text-[0.75rem] leading-none">{nickname || ' '}</p>

        <ul className="mt-2.5 flex flex-col gap-1.5">
          {takers.length === 0 && <li className="text-ink-dim text-body">—</li>}
          {takers.map((pick) => (
            <li key={pick.user_id} className="flex items-center gap-1.5">
              <span
                className={cn(
                  'text-body min-w-0 truncate',
                  outcome === 'wrong' && 'text-ink-dim line-through',
                  outcome === 'trailing' && 'text-ink-dim',
                  outcome === 'correct' && 'text-ink font-semibold',
                )}
              >
                {nameByUser.get(pick.user_id) ?? 'Unknown member'}
              </span>
              {pick.is_lock && (
                <Lock
                  className={cn('size-3 shrink-0', LOCK_TONE_CLASS[lockTone(outcome)])}
                  aria-label="Lock of the week"
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return (
    <ul className="flex flex-col gap-2.5">
      {games.map((game) => {
        const forGame = byGame.get(game.id) ?? []
        const picked = new Set(forGame.map((pick) => pick.user_id))
        // Named rather than counted: on a locked week this is the forfeit list,
        // and "3 no picks" is the one number nobody can act on.
        const missing = members.filter((member) => !picked.has(member.userId))

        return (
          <li key={game.id} className="bg-card border-rule rounded-row border px-4 py-3.5">
            <p className="eyebrow">{gameStatusLabel(game, kickoffLabel)}</p>

            <div className="mt-2.5 flex items-start gap-4">
              {side(game, game.away_team, forGame)}
              <div className="bg-rule w-px self-stretch" aria-hidden />
              {side(game, game.home_team, forGame)}
            </div>

            {missing.length > 0 && (
              <p className="text-ink-dim border-rule mt-3 border-t pt-2.5 text-[0.75rem] leading-snug">
                <span className="eyebrow">No pick</span>{' '}
                {missing.map((member) => member.name).join(', ')}
              </p>
            )}
          </li>
        )
      })}
    </ul>
  )
}
